import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Target, Lightbulb, Users, Briefcase, Shield, TrendingUp, Award, CheckCircle, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import ServiceModal from '@/components/ServiceModal';

export default function Services() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const services = [
    {
      icon: Users,
      title: 'Permanent Staffing',
      shortDescription: 'Our permanent placement services are meticulously crafted to ensure that you find the perfect candidate for long-term success.',
      features: [
        'Tailored recruitment process aligned with your culture',
        'Thorough candidate vetting and assessment',
        'Cultural alignment and fit evaluation',
        'Post-placement support and follow-up'
      ],
      fullContent: {
        intro: 'Our permanent placement services at Symetrix are meticulously crafted to ensure that you find the perfect candidate for long-term success. We understand that hiring permanent employees is a significant investment in your organization\'s future, which is why we go above and beyond to thoroughly vet candidates and match them with your company culture and job requirements.',
        sections: [
          {
            title: 'Tailored Recruitment Process',
            content: 'Our permanent placement process begins with a comprehensive understanding of your organization\'s culture, values, and strategic goals. We collaborate closely with you to define the specific qualifications, skills, and attributes you are seeking in a permanent employee.'
          },
          {
            title: 'Thorough Candidate Vetting',
            content: 'We employ a rigorous vetting process to ensure that we present only the most qualified candidates for your consideration. This includes in-depth interviews, reference checks, and assessments to evaluate candidates\' technical skills, experience, and cultural fit.'
          },
          {
            title: 'Cultural Alignment',
            content: 'We recognize the importance of cultural fit in long-term employee success. Our consultants take the time to understand your company culture and values, allowing us to identify candidates who not only have the necessary skills and experience but also align with your organizational ethos.'
          },
          {
            title: 'Comprehensive Assessment',
            content: 'We conduct thorough assessments to evaluate candidates\' suitability for the role and your organization. This includes evaluating their past performance, accomplishments, career aspirations, and potential for growth within your company.'
          },
          {
            title: 'Transparent Communication',
            content: 'Throughout the recruitment process, we maintain open and transparent communication with both you and the candidates. We provide regular updates on the progress of the search and facilitate constructive feedback to ensure alignment and mutual understanding.'
          },
          {
            title: 'Post-Placement Support',
            content: 'Our commitment to your success doesn\'t end with the placement of a candidate. We follow up with both you and the new employee to ensure a smooth transition and integration into your organization. Our goal is to facilitate a positive onboarding experience and set the stage for long-term success.'
          }
        ],
        benefits: [
          'Quality Candidates: We present you with candidates who not only meet your technical requirements but also fit seamlessly into your company culture.',
          'Long-Term Success: Our focus on finding the right match ensures that you hire employees who are committed to your organization\'s success and stay with you for the long term.',
          'Time and Cost Savings: By outsourcing your permanent recruitment needs to us, you save valuable time and resources that can be allocated to other strategic initiatives.'
        ],
        whyChoose: [
          'Expertise: Our team of experienced recruiters has a deep understanding of various industries and specializes in identifying top talent.',
          'Personalized Service: We take a personalized approach to recruitment, tailoring our services to meet your specific needs and objectives.',
          'Commitment to Excellence: We are committed to delivering exceptional results and exceeding your expectations at every stage of the recruitment process.'
        ]
      }
    },
    {
      icon: Briefcase,
      title: 'Temporary and Contract Staffing',
      shortDescription: 'Flexible staffing solutions tailored to your specific needs for managing workload fluctuations and special projects.',
      features: [
        'Temporary staffing for short-term roles',
        'Contract staffing for project-based needs',
        'Access to pre-screened professionals',
        'Scalable workforce solutions'
      ],
      fullContent: {
        intro: 'Need temporary or contract staff? Symetrix understands that managing workload fluctuations and special projects can be challenging. That\'s why we offer flexible staffing solutions tailored to your specific needs.',
        sections: [
          {
            title: 'Temporary Staffing',
            content: 'Our temporary staffing solutions provide you with access to qualified professionals who can fill short-term roles and help you manage workload peaks. Whether you need extra support during busy seasons, to cover employee absences, or to complete special projects, we can quickly supply skilled temporary staff to meet your requirements.'
          },
          {
            title: 'Contract Staffing',
            content: 'Contract staffing offers a flexible solution for medium to long-term staffing needs. If you have a specific project or initiative that requires specialized expertise, our contract staffing services allow you to hire professionals on a fixed-term basis. This arrangement provides you with the flexibility to scale your workforce up or down as needed without the long-term commitment of permanent employment.'
          }
        ],
        benefits: [
          'Scalability: Our flexible staffing solutions allow you to adjust your workforce size in response to changing business demands. Whether you need to ramp up staffing levels quickly or scale back during slower periods, we can provide the right talent at the right time.',
          'Cost-Effectiveness: Flexible staffing helps you control labor costs by only paying for the resources you need, when you need them. This allows you to optimize your budget and allocate resources more efficiently.',
          'Access to Specialized Skills: Our extensive network of professionals includes individuals with a wide range of skills and expertise. Whether you need IT specialists, project managers, administrative support, or any other role, we can connect you with qualified professionals who have the specific skills you require.',
          'Reduced Administrative Burden: We handle all aspects of recruitment, onboarding, payroll, and compliance for temporary and contract staff, freeing up your time and resources to focus on your core business activities.'
        ],
        whyChoose: [
          'Industry Expertise: Our team has extensive experience across various industries, allowing us to understand your unique staffing requirements and provide tailored solutions.',
          'Quality Assurance: We rigorously screen and vet all candidates to ensure they meet your standards and expectations.',
          'Speed and Efficiency: With our vast network and streamlined processes, we can quickly supply qualified staff to meet your immediate needs.',
          'Personalized Service: We take the time to understand your business and tailor our staffing solutions to meet your specific needs and objectives.'
        ]
      }
    },
    {
      icon: Target,
      title: 'Executive Search',
      shortDescription: 'Meticulously designed services to identify and recruit top-tier executives who can drive your business forward.',
      features: [
        'Tailored executive search process',
        'Extensive network and industry expertise',
        'Rigorous candidate assessment',
        'Confidential and discreet approach'
      ],
      fullContent: {
        intro: 'Finding the right leaders for your organization is crucial. At Symetrix HR Consulting, our executive search services are meticulously designed to identify and recruit top-tier executives who can drive your business forward and ensure long-term success.',
        sections: [
          {
            title: 'Tailored Executive Search',
            content: 'Our executive search process is highly customized to meet the unique needs of your organization. We begin by gaining a deep understanding of your company\'s vision, goals, and culture. This insight allows us to identify the leadership qualities and expertise required to propel your business to new heights.'
          },
          {
            title: 'Extensive Network and Expertise',
            content: 'Leveraging our extensive network and industry expertise, we have access to a vast pool of executive talent across various sectors. Our experienced consultants are well-versed in identifying individuals who have a proven track record of success and the strategic vision to lead effectively.'
          },
          {
            title: 'Rigorous Candidate Assessment',
            content: 'We utilize a rigorous assessment process to ensure that we present only the most qualified candidates. This includes in-depth interviews, comprehensive background checks, and detailed evaluations of each candidate\'s professional achievements, leadership style, and cultural fit. Our goal is to ensure that the executives we recommend are not only highly skilled but also align seamlessly with your organization\'s values and objectives.'
          },
          {
            title: 'Confidential and Discreet Approach',
            content: 'We understand the sensitive nature of executive recruitment and maintain the highest levels of confidentiality and discretion throughout the process. Our careful handling of information ensures that both clients and candidates can engage in the search process with complete trust and security.'
          },
          {
            title: 'Strategic Consultation',
            content: 'Our executive search services go beyond just finding candidates. We act as strategic consultants, providing valuable insights and advice on the competitive landscape, market trends, and compensation benchmarks. This strategic support helps you make informed decisions that align with your long-term business strategy.'
          },
          {
            title: 'Comprehensive Search Process',
            content: 'Our process includes: Client Consultation, Search Strategy Development, Candidate Identification, In-Depth Interviews, Presentation of Shortlist, and Final Selection and Negotiation. We assist throughout the entire journey to ensure a smooth transition for the new executive.'
          },
          {
            title: 'Long-Term Success',
            content: 'Our commitment to your organization doesn\'t end with the placement of an executive. We follow up with both the client and the new leader to ensure a successful integration and long-term success. This ongoing support helps to maximize the impact of the new hire and contributes to the overall growth and stability of your organization.'
          }
        ],
        benefits: [
          'Access to Top-Tier Talent: We identify and recruit senior executives who have proven track records of success and the strategic vision needed to lead effectively.',
          'Confidential Process: Our discreet and confidential approach ensures that both clients and candidates can engage in the search process with complete trust.',
          'Strategic Partnership: Beyond recruitment, we act as consultants providing valuable insights on market trends, compensation benchmarks, and competitive landscape.',
          'Long-Term Success: Our commitment extends beyond placement with ongoing support to ensure successful integration and maximize the impact of the new executive.'
        ],
        whyChoose: [
          'Deep Industry Expertise: Our consultants have extensive experience across various sectors and understand what it takes to lead at the executive level.',
          'Extensive Network: Access to a vast pool of executive talent that may not be actively seeking new opportunities.',
          'Rigorous Assessment: Comprehensive evaluation process that goes beyond skills to assess cultural fit and leadership style.',
          'Proven Track Record: A history of successful executive placements that have driven organizational growth and transformation.'
        ]
      }
    },
    {
      icon: Lightbulb,
      title: 'Consulting and Advisory',
      shortDescription: 'Comprehensive consulting services to support your talent management strategies and optimize your overall approach.',
      features: [
        'Workforce planning and strategy',
        'Market analysis and insights',
        'Employer branding development',
        'Diversity and inclusion initiatives'
      ],
      fullContent: {
        intro: 'Our consulting and advisory services at Symetrix help organizations optimize their talent strategies and build sustainable competitive advantages. We provide expert guidance across all aspects of talent management and HR operations.',
        sections: [
          {
            title: 'Strategic Workforce Planning',
            content: 'We work with you to develop comprehensive workforce strategies that align with your business objectives. Our consultants analyze your current talent landscape and future needs to create actionable plans.'
          },
          {
            title: 'Talent Acquisition Strategy',
            content: 'Optimize your hiring processes with our proven methodologies. From employer branding to candidate experience, we help you attract and secure top talent in competitive markets.'
          },
          {
            title: 'Market Intelligence',
            content: 'Stay ahead with our market insights and competitive analysis. We provide data-driven recommendations on compensation, benefits, and talent trends specific to your industry.'
          },
          {
            title: 'Diversity & Inclusion',
            content: 'Build diverse, equitable, and inclusive teams with our specialized consulting services. We help implement programs that foster belonging and drive organizational success.'
          }
        ],
        benefits: [
          'Strategic Advantage: Gain competitive insights and data-driven recommendations that help you attract and retain top talent.',
          'Cost Efficiency: Optimize your talent acquisition budget and reduce time-to-hire with proven strategies and processes.',
          'Scalable Solutions: Flexible consulting services that grow with your organization and adapt to changing business needs.',
          'Expert Guidance: Benefit from decades of combined experience in talent management and HR operations.'
        ],
        whyChoose: [
          'Customized Approach: We tailor our consulting services to your unique organizational culture and business objectives.',
          'Measurable Results: Focus on delivering quantifiable improvements in hiring quality, retention rates, and employee satisfaction.',
          'Industry Insights: Stay ahead with the latest trends, best practices, and market intelligence specific to your sector.',
          'Partnership Model: We work alongside your team as trusted advisors, not just external consultants.'
        ]
      }
    }
  ];

  const filteredServices = searchQuery
    ? services.filter(s => 
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.shortDescription.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : services;

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-teal-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <h1 
              className="text-5xl sm:text-6xl font-bold mb-6"
              data-testid="services-hero-title"
              data-anim="typing"
              data-speed="35"
            >
              Our Services
            </h1>
            <p 
              className="text-xl text-blue-50 max-w-3xl mx-auto mb-12"
              data-anim="fade-up"
              data-delay="120"
            >
              Comprehensive talent solutions designed for <span
                data-anim="rotating-text"
                data-words="your success|exceptional results|strategic growth|business transformation|competitive advantage"
                data-speed="2500"
                data-transition="500"
                className="font-semibold text-transparent text-2xl bg-clip-text bg-[linear-gradient(90deg,#fbbf24,#f59e0b,#fcd34d)] bg-[length:200%_100%] animate-[gradient-move_3s_linear_infinite]"
              >your success</span>.
            </p>
            
            {/* Search Bar */}
            <div 
              className="max-w-2xl mx-auto"
              data-anim="fade-up"
              data-delay="250"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  placeholder="Search services..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 bg-white text-gray-900 border-none"
                  data-testid="services-search"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Symetrix */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              Why Choose Symetrix
            </h2>
            <p 
              className="text-lg text-gray-600"
              data-anim="fade-up"
              data-delay="100"
            >
              Discover the advantages that set us apart in the talent consulting industry
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-anim="list-stagger" data-stagger="120">
            <Card className="border-none shadow-lg hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Unmatched Expertise</h3>
                <p className="text-gray-600">Over 14 years of industry experience with a proven track record of successful placements.</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="text-green-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovative Approach</h3>
                <p className="text-gray-600">Leveraging cutting-edge technology and data analytics for superior results.</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Client-Centric Solutions</h3>
                <p className="text-gray-600">Tailored strategies that align with your unique organizational needs and culture.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Core Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              Our Core Services
            </h2>
            <p 
              className="text-lg text-gray-600 max-w-3xl mx-auto"
              data-anim="fade-up"
              data-delay="100"
            >
              Comprehensive talent solutions designed to meet your organization's unique challenges
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10" data-anim="list-stagger" data-stagger="150">
            {filteredServices.map((service, index) => {
              const IconComponent = service.icon;
              
              return (
                <Card 
                  key={index}
                  className="border-none shadow-lg overflow-hidden hover:shadow-xl transition-shadow h-full min-h-[420px] md:min-h-[460px]"
                >
                  <CardContent className="p-0 h-full">
                    <div className="flex flex-col h-full">
                      {/* Icon Section - fixed height banner at top */}
                      <div
                        className={`flex-shrink-0 w-full h-40 md:h-48 ${
                          index === 0
                            ? 'bg-gradient-to-br from-green-500 to-green-700'
                            : index === 1
                            ? 'bg-gradient-to-br from-teal-500 to-cyan-700'
                            : index === 2
                            ? 'bg-gradient-to-br from-blue-500 to-blue-700'
                            : 'bg-gradient-to-br from-orange-500 to-orange-700'
                        } p-8 flex flex-col items-center justify-center text-center`}
                      >
                        <div>
                          <IconComponent size={64} className="text-white" />
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="flex-1 p-10">
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">{service.title}</h3>
                        <p className="text-gray-600 mb-6 text-lg">{service.shortDescription}</p>
                        <div className="space-y-3 mb-8">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <CheckCircle className={`${
                                index === 0 ? 'text-green-600' :
                                index === 1 ? 'text-teal-600' :
                                index === 2 ? 'text-blue-600' :
                                'text-orange-600'
                              } flex-shrink-0 mt-1`} size={20} />
                              <span className="text-gray-700 text-base">{feature}</span>
                            </div>
                          ))}
                        </div>
                        <Button 
                          className={`${
                            index === 0 ? 'bg-green-500 hover:bg-green-700' :
                            index === 1 ? 'bg-teal-500 hover:bg-teal-700' :
                            index === 2 ? 'bg-blue-500 hover:bg-blue-700' :
                            'bg-orange-400 hover:bg-orange-700'
                          } text-white transition-colors`}
                          size="lg"
                          onClick={() => {
                            setSelectedService({
                              ...service,
                              colorScheme: index === 0 ? 'green' : index === 1 ? 'teal' : index === 2 ? 'blue' : 'orange'
                            });
                            setIsModalOpen(true);
                          }}
                          data-testid={`learn-more-${index}`}
                        >
                          Learn More →
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Service Modal */}
          <ServiceModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedService(null);
            }}
            service={selectedService}
            colorScheme={selectedService?.colorScheme || 'blue'}
          />
        </div>
      </section>
      
      {/* Areas of Expertise */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              Areas of Expertise
            </h2>
            <p 
              className="text-lg text-gray-600 max-w-4xl mx-auto"
              data-anim="fade-up"
              data-delay="100"
            >
              Symetrix HR Consulting delivers specialist hiring and talent solutions across Technology, Data & AI, Cloud & Infrastructure, Finance, Legal & Compliance, HR, and Operations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-anim="list-stagger" data-stagger="100">
            {/* Cloud & Infrastructure */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-600 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cloud & Infrastructure</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Technologies:</strong> AWS, Azure, GCP, IBM Cloud, Oracle Cloud, Salesforce, Heroku
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> Cloud Architects, DevOps, SRE, Cloud DBAs, Network & Monitoring specialists
                </p>
              </CardContent>
            </Card>

            {/* Software & Product Engineering */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-green-600 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Software & Product Engineering</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Technologies:</strong> Java, .NET, Python, C/C++, React, Angular, Backend, Frontend, Full-stack, Mobile, Embedded
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> Product Developers, Engineering Managers, Platform Engineers, Automation Framework Developers
                </p>
              </CardContent>
            </Card>

            {/* Data & Analytics / AI/ML */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-600 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Data & Analytics / AI/ML</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Focus:</strong> Data Engineering, BI (Tableau, Power BI, Qlik), Machine Learning, NLP, LLMs, Generative AI
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> Data Engineers, Data Scientists, ML/AI Engineers, Biostatisticians, Econometricians
                </p>
              </CardContent>
            </Card>

            {/* Databases & Platform */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-500 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Databases & Platform</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Technologies:</strong> Oracle, SQL Server, PostgreSQL, MongoDB, MySQL, DB2, RTOS
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> Database Architects, Cloud DBAs, Database Engineers, Platform specialists
                </p>
              </CardContent>
            </Card>

            {/* Cyber Security & Networking */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-600 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cyber Security & Networking</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Focus:</strong> Identity & Access, Cloud Security, Incident Management, Network & Application Security
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> Security Engineers, Incident Managers, Risk & Compliance specialists
                </p>
              </CardContent>
            </Card>

            {/* Product & Technology Leadership */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-green-700 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Product & Technology Leadership</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Focus:</strong> Solution Architecture, Tech & Data Architecture, Platform Engineering
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> Solution Architects, Technical Architects, Core Architects, Platform Engineers
                </p>
              </CardContent>
            </Card>

            {/* Finance, Banking & Capital Markets */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-700 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Finance, Banking & Capital Markets</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Focus:</strong> FP&A, Investment Banking Operations, Audit, Structured Finance, Taxation, Corporate Finance
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> Financial Analysts, Controllers, Auditors, Tax specialists, Trade Accounting
                </p>
              </CardContent>
            </Card>

            {/* Legal, Risk & Compliance */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-pink-600 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Legal, Risk & Compliance</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Focus:</strong> IP, Contract Management, SOX, TPRM, Forensics, Regulatory Compliance
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> Legal Counsel, Compliance Officers, Risk Managers, E-Discovery specialists
                </p>
              </CardContent>
            </Card>

            {/* Human Capital & HR Ops */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-teal-700 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Human Capital & HR Ops</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Focus:</strong> Talent Acquisition, HRBP, Global Mobility, HR Analytics, OD, Performance Management
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> HR Business Partners, Talent Acquisition, L&D, Employee Engagement
                </p>
              </CardContent>
            </Card>

            {/* Operations & Support */}
            <Card className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-green-600 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Operations & Support</h3>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Focus:</strong> PMO, Implementation, Incident & Escalation Management, Front Office & Facilities
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Roles:</strong> PMO Managers, Implementation Engineers, Incident Managers, Operations Support
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold text-gray-900 mb-4"
              data-anim="fade-up"
            >
              Industries Served
            </h2>
            <p 
              className="text-lg text-gray-600 max-w-3xl mx-auto"
              data-anim="fade-up"
              data-delay="100"
            >
              We deliver specialized talent solutions across diverse sectors, understanding the unique challenges and requirements of each industry.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-anim="list-stagger" data-stagger="80">
            {[
              'Financial Services',
              'Hedge Funds',
              'HFT / Algo Trading',
              'FinTech',
              'Technology',
              'SaaS',
              'Enterprise Software',
              'Healthcare',
              'Professional Services',
              'Investment Banking',
              'Insurance',
              'Real Estate'
            ].map((industry, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all text-center">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold text-gray-900">{industry}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-bold mb-4"
            data-anim="fade-up"
          >
            Request a Consultation
          </h2>
          <p 
            className="text-xl mb-8 text-blue-100"
            data-anim="fade-up"
            data-delay="100"
          >
            Let's discuss how our services can help you achieve your talent acquisition goals.
          </p>
          <Link to="/contact" data-testid="request-consultation-button">
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 transform hover:scale-105 transition-all"
              data-anim="scale-in"
              data-delay="200"
            >
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
